import { useState, useEffect } from 'react';

interface CookieOptions {
  path?: string;
  expires?: Date | number;
  maxAge?: number;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

const COOKIE_PREFERENCES_KEY = 'cookie_preferences';
const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false
};

export const useCookies = () => {
  const [cookies, setCookies] = useState<{ [key: string]: string }>({});
  const [cookiePreferences, setCookiePreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES);

  useEffect(() => {
    // Charger les cookies au montage
    const loadCookies = () => {
      const allCookies = document.cookie.split(';').reduce((acc, current) => {
        const [key, value] = current.trim().split('=');
        return { ...acc, [key]: decodeURIComponent(value) };
      }, {} as { [key: string]: string });
      
      setCookies(allCookies);

      // Charger les préférences de cookies
      const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPreferences) {
        try {
          const preferences = JSON.parse(savedPreferences);
          setCookiePreferences(preferences);
        } catch (error) {
          console.error('Erreur lors du chargement des préférences de cookies:', error);
        }
      }
    };

    loadCookies();
  }, []);

  const setCookie = (
    name: string,
    value: string,
    options: CookieOptions = {}
  ) => {
    const {
      path = '/',
      expires,
      maxAge,
      domain,
      secure,
      sameSite = 'Lax'
    } = options;

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (path) cookieString += `; path=${path}`;
    if (domain) cookieString += `; domain=${domain}`;
    if (secure) cookieString += '; secure';
    if (sameSite) cookieString += `; samesite=${sameSite}`;

    if (expires) {
      if (typeof expires === 'number') {
        cookieString += `; expires=${new Date(expires).toUTCString()}`;
      } else {
        cookieString += `; expires=${expires.toUTCString()}`;
      }
    }

    if (maxAge) cookieString += `; max-age=${maxAge}`;

    document.cookie = cookieString;
    setCookies(prev => ({ ...prev, [name]: value }));
  };

  const getCookie = (name: string): string | undefined => {
    return cookies[name];
  };

  const removeCookie = (name: string, options: CookieOptions = {}) => {
    setCookie(name, '', { ...options, maxAge: -1 });
    setCookies(prev => {
      const { [name]: _, ...rest } = prev;
      return rest;
    });
  };

  const hasAcceptedCookies = (): boolean | undefined => {
    const preferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (!preferences) return undefined;
    
    try {
      const parsedPreferences = JSON.parse(preferences);
      return Object.values(parsedPreferences).some(value => value === true);
    } catch {
      return undefined;
    }
  };

  const acceptCookies = (preferences?: Partial<CookiePreferences>) => {
    const newPreferences = {
      ...DEFAULT_PREFERENCES,
      ...preferences
    };
    
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(newPreferences));
    setCookiePreferences(newPreferences);

    // Définir les cookies en fonction des préférences
    if (newPreferences.analytics) {
      setCookie('analytics_enabled', 'true', {
        maxAge: 365 * 24 * 60 * 60,
        sameSite: 'Strict'
      });
    }

    if (newPreferences.marketing) {
      setCookie('marketing_enabled', 'true', {
        maxAge: 365 * 24 * 60 * 60,
        sameSite: 'Strict'
      });
    }

    if (newPreferences.preferences) {
      setCookie('preferences_enabled', 'true', {
        maxAge: 365 * 24 * 60 * 60,
        sameSite: 'Strict'
      });
    }
  };

  const rejectCookies = () => {
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(DEFAULT_PREFERENCES));
    setCookiePreferences(DEFAULT_PREFERENCES);

    // Supprimer tous les cookies non essentiels
    removeCookie('analytics_enabled');
    removeCookie('marketing_enabled');
    removeCookie('preferences_enabled');
  };

  const getPreferences = (): CookiePreferences => {
    return cookiePreferences;
  };

  return {
    setCookie,
    getCookie,
    removeCookie,
    hasAcceptedCookies,
    acceptCookies,
    rejectCookies,
    getPreferences,
    cookiePreferences
  };
}; 